/**
 * Authentication hooks using TanStack Query
 *
 * Provides React Query hooks for authentication-related API calls
 */

import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../api/axios.config';
import { UserProfile } from '../interface/auth';

/**
 * Hook to fetch authenticated user's profile
 * Uses TanStack Query for caching and state management
 *
 * @returns useQuery result with UserProfile data
 *
 * @example
 * const { data: userProfile, isLoading, error } = useUserProfile();
 */
export function useUserProfile() {
  return useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const response = await axiosInstance.get<UserProfile>('/auth/profile');
      return response.data;
    },
    retry: false, // Don't retry on 401
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Hook to fetch protected example endpoint
 * Demonstrates authenticated API call pattern with TanStack Query
 *
 * @returns useQuery result with protected data
 *
 * @example
 * const { data, isLoading, error } = useProtectedExample();
 */
export function useProtectedExample() {
  return useQuery({
    queryKey: ['protected', 'example'],
    queryFn: async () => {
      const response = await axiosInstance.get<{
        message: string;
        user: { id: string; email: string; userType: string };
      }>('/protected/example');
      return response.data;
    },
    retry: false, // Don't retry on 401
  });
}

/**
 * Hook to check if current user is an admin
 * Uses the user profile data to determine admin status
 *
 * @returns Object with isAdmin boolean and loading state
 *
 * @example
 * const { isAdmin, isLoading } = useIsAdmin();
 */
export function useIsAdmin() {
  const { data: userProfile, isLoading } = useUserProfile();

  return {
    isAdmin: userProfile?.isAdmin ?? false,
    isLoading,
  };
}